import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  Settings,
  Users,
  DollarSign,
  Package
} from "lucide-react";
import { ProductStatusCard } from "../ProductStatusCard";
import { useWorkstationStore } from "@/hooks/useWorkstationStore";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

export const SubmissionConsole = () => {
  const {
    getFilteredProducts,
    getReadyProducts,
    getNeedsFixProducts,
    getBlockedProducts,
    selectedProducts,
    selectAllProducts,
    clearSelection,
    toggleProductSelection,
    globalMultiplier,
    setGlobalMultiplier,
    getProductStats,
    isSubmitting,
    setSubmitting
  } = useWorkstationStore();

  const [activeTab, setActiveTab] = useState<'ready' | 'needs-fix' | 'blocked'>('ready');
  const [showFamilyReview, setShowFamilyReview] = useState(false);

  const readyProducts = getReadyProducts();
  const needsFixProducts = getNeedsFixProducts();
  const blockedProducts = getBlockedProducts();
  const stats = getProductStats();

  const readinessProgress = stats.total > 0 ? (stats.ready / stats.total) * 100 : 0;

  const getTabProducts = () => {
    switch (activeTab) {
      case 'ready':
        return readyProducts;
      case 'needs-fix':
        return needsFixProducts;
      case 'blocked':
        return blockedProducts;
      default:
        return [];
    }
  };

  const currentProducts = getTabProducts();
  const allSelected = selectedProducts.size === currentProducts.length && currentProducts.length > 0;

  const handleSelectAllCurrentTab = () => {
    if (allSelected) {
      // Deselect all from current tab
      currentProducts.forEach(product => {
        if (selectedProducts.has(product.id)) {
          toggleProductSelection(product.id);
        }
      });
    } else {
      // Select all from current tab
      currentProducts.forEach(product => {
        if (!selectedProducts.has(product.id)) {
          toggleProductSelection(product.id);
        }
      });
    }
  };

  const handleSubmitClick = async () => {
    if (readyProducts.length === 0) return;

    setSubmitting(true);
    try {
      // Simulate submission process
      console.log(`Submitting ${readyProducts.length} products to Square...`);
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In real implementation, this would trigger the actual submission logic
      console.log("Products submitted successfully!");
      
    } catch (error) {
      console.error("Submission failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFamilyReview = () => {
    setShowFamilyReview(true);
    // Show family products review modal
    console.log("Opening family products review...");
  };

  const handleBulkCategoryUpdate = () => {
    const selectedProductIds = Array.from(selectedProducts);
    console.log("Bulk category update for products:", selectedProductIds);
  };

  const handleBulkPriceUpdate = () => {
    const selectedProductIds = Array.from(selectedProducts);
    console.log("Bulk price update for products:", selectedProductIds);
  };

  const tabConfig = {
    ready: {
      label: 'Ready for Submission',
      count: stats.ready,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    'needs-fix': {
      label: 'Needs Manual Work',
      count: stats.needsFix,
      icon: AlertCircle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    blocked: {
      label: 'Blocked Items',
      count: stats.blocked,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200'
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Global Controls */}
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Final Review & Submission</h2>
          
          {/* Global Multiplier Control */}
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-200">
            <Settings className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">Multiplier:</span>
            <span className="text-xs text-blue-700">×</span>
            <Input
              type="number"
              step="0.1"
              min="1.0"
              max="5.0"
              value={globalMultiplier}
              onChange={(e) => setGlobalMultiplier(parseFloat(e.target.value) || 1.5)}
              className="w-16 h-7 text-sm text-center border-blue-200"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={handleFamilyReview}
            className="flex items-center gap-2"
          >
            <Users className="w-4 h-4" />
            Review Families
          </Button>
          
          <Button 
            onClick={handleSubmitClick}
            disabled={isSubmitting || readyProducts.length === 0}
            className="flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            {isSubmitting ? 'Submitting...' : `Submit ${readyProducts.length} Products`}
          </Button>
        </div>
      </div>

      {/* Readiness Progress */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Submission Readiness</span>
            <span className="text-sm text-gray-500">{readinessProgress.toFixed(0)}%</span>
          </div>
          <Progress value={readinessProgress} className="w-full mb-2" />
          <div className="flex justify-between text-xs text-gray-500">
            <span>{stats.ready} ready</span>
            <span>{stats.needsFix + stats.blocked} need attention</span>
          </div>
        </CardContent>
      </Card>

      {/* Status Tabs */}
      <div className="flex gap-2 border-b">
        {(Object.keys(tabConfig) as Array<keyof typeof tabConfig>).map((tab) => {
          const config = tabConfig[tab];
          const Icon = config.icon;
          const isActive = activeTab === tab;
          
          return (
            <Button
              key={tab}
              variant="ghost"
              onClick={() => setActiveTab(tab)}
              className={cn(
                "flex items-center gap-2 px-4 py-2 border-b-2 transition-colors",
                isActive 
                  ? `border-current ${config.color}` 
                  : "border-transparent text-gray-600 hover:text-gray-800"
              )}
            >
              <Icon className="w-4 h-4" />
              {config.label} ({config.count})
            </Button>
          );
        })}
      </div>

      {/* Bulk Actions Bar */}
      {currentProducts.length > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAllCurrentTab}
                />
                <span className="text-sm">
                  {selectedProducts.size > 0 
                    ? `${selectedProducts.size} selected from ${activeTab} products` 
                    : `Select all ${currentProducts.length} ${activeTab} products`
                  }
                </span>
              </div>

              {selectedProducts.size > 0 && (
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleBulkCategoryUpdate}
                  >
                    Assign Category
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleBulkPriceUpdate}
                  >
                    <DollarSign className="w-3 h-3 mr-1" />
                    Update Pricing
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearSelection}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products Display */}
      <Card>
        <CardHeader>
          <CardTitle className={cn("flex items-center gap-2", tabConfig[activeTab].color)}>
            {React.createElement(tabConfig[activeTab].icon, { className: "w-5 h-5" })}
            {tabConfig[activeTab].label} ({currentProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentProducts.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab.replace('-', ' ')} products
              </h3>
              <p className="text-gray-600">
                {activeTab === 'ready' && "Products will appear here once they have costs and categories assigned."}
                {activeTab === 'needs-fix' && "Products with partial data will appear here."}
                {activeTab === 'blocked' && "Products missing both cost and category will appear here."}
              </p>
            </div>
          ) : (
            <ScrollArea className="h-96">
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {currentProducts.map((product) => (
                  <ProductStatusCard
                    key={product.id}
                    product={product}
                    isSelected={selectedProducts.has(product.id)}
                    showSelection
                    variant="detailed"
                  />
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Submission Summary */}
      {readyProducts.length > 0 && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <div className="font-medium text-green-900">
                    {readyProducts.length} products ready for Square sync
                  </div>
                  <div className="text-sm text-green-700">
                    Estimated retail value: $
                    {readyProducts.reduce((total, product) => {
                      return total + (product.costPerUnit ? product.costPerUnit * globalMultiplier * product.boxes * product.unitsPerBox : 0);
                    }, 0).toFixed(2)}
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={handleSubmitClick}
                disabled={isSubmitting}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmitting ? 'Processing...' : 'Submit to Square'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};